import { isError } from '@game/utils';
import { stopMusic } from '@lib/musicManager';
import { socketManager } from '@lib/socketManager';

import { authStore } from '@store/authStore';
import { combatStore } from '@store/combatStore';
import { persistentStore } from '@store/persistentStore';
import { store } from '@store/store';

import type { AuthInstanceInterface } from '../context/Auth/AuthContext';
import { socketEventHandler } from '../context/Socket/socketEventHandler';

let isInitialized = false;

/**
 * Initialize auth-related event subscriptions.
 * Should be called once during app initialization after auth instance is created.
 * Uses a guard to prevent duplicate initialization (e.g., from React StrictMode).
 */
export function initializeAuthSubscriptions(auth: AuthInstanceInterface): void {
  if (isInitialized) {
    return;
  }
  isInitialized = true;
  const { handleError, handleLogout, handleAuth } = authStore.getState();

  auth.onLogin((idToken: string, anonymous: boolean) => {
    if (idToken) {
      if (socketManager.isConnected()) {
        return;
      }
      // Store the UID so the storage event handler can reject cross-tab rehydration
      // from a different user's session (see handleStorage in AuthenticatedView).
      persistentStore.setState({ uid: auth.getCurrentUser()?.uid ?? '' });
      handleAuth(idToken);
      persistentStore.getState().setExpectSignIn(true);
      authStore.setState({ anonymous });
      const websocketUrl = import.meta.env.VITE_GAME_SERVER_WEBSOCKET;
      if (!websocketUrl) {
        // Avoid connecting to an unintended origin when the game server URL is not configured.
        handleError(new Error('Missing GAME_SERVER_WEBSOCKET environment variable'));
        return;
      }
      socketManager.connect(idToken, websocketUrl, socketEventHandler);
    }
  });

  auth.onError((error: unknown) => {
    if (isError(error)) {
      handleError(error);
    }
  });

  auth.onLogout(async () => {
    persistentStore.setState({ uid: '' });
    persistentStore.getState().setExpectSignIn(false);
    handleLogout();
    socketManager.disconnect();
    store.getState().resetState();
    combatStore.getState().resetCombat();
    await stopMusic();
  });
}

/**
 * Reset the initialization flag. Used for cleanup.
 */
export function cleanupAuthSubscriptions(): void {
  isInitialized = false;
}
